/* eslint-disable no-undef */
import { Typography, useMediaQuery } from '@mui/material'
import { Dialog } from 'components/Dialog/Dialog'
import { DropdownOption } from 'components/IconDropdown/IconDropdown'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { Toolbar } from 'components/Toolbar/Toolbar'
import {
  useDeleteArea,
  useDeleteAreaGroup,
  usePatchNewAreaCoordinates,
  usePostNewAreaCoordinates,
} from 'hooks/useServiceAreas'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useState,
  useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import { EDIT_DELIVERY_AREA_TYPE } from 'types/api/serviceAreas'
import { DeliveryArea } from 'types/model/serviceAreas'
import { CopyAreaModal } from './CopyAreaModal'
import { EditCollectionPointModal } from './EditCollectionPointModal'
import { EditCoordinatesModal } from './EditCoordinatesModal'
import { ServiceAreaSettingsModal } from './ServiceAreaSettingsModal'
import { hasNoDeliveryAreas } from './utils'

const EDIT = 'Edit'
const DELETE = 'Delete'
const DELETE_GROUP = 'Delete Group'

type ServiceAreaToolbarProps = {
  isEditing: boolean
  deliveryAreaId: number | null
  drawnPath: { lat: number; lng: number }[][] | []
  drawingModeEnabled: number | null
  deleteShapes: (overlays: google.maps.Polygon[] | []) => void
  overlays: google.maps.Polygon[] | []
  setDrawingModeEnabled: Dispatch<SetStateAction<number | null>>
  setDrawnPath: Dispatch<SetStateAction<{ lat: number; lng: number }[][] | []>>
  setIsEditing: Dispatch<SetStateAction<boolean>>
  setDeliveryAreaId: Dispatch<SetStateAction<number | null>>
}

export const ServiceAreaToolbar = ({
  isEditing,
  deliveryAreaId,
  drawnPath,
  drawingModeEnabled,
  deleteShapes,
  overlays,
  setDrawingModeEnabled,
  setDrawnPath,
  setIsEditing,
  setDeliveryAreaId,
}: ServiceAreaToolbarProps) => {
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false)
  const [copyModal, setCopyModal] = useState({ right: false })
  const [settingsModal, setSettingsModal] = useState({
    right: false,
  })
  const [collectionPointEditModal, setCollectionPointEditModal] = useState({
    right: false,
  })
  const [editCoordinatesModal, setEditCoordinatesModal] = useState({
    right: false,
  })
  const [editType, setEditType] = useState<EDIT_DELIVERY_AREA_TYPE>(
    EDIT_DELIVERY_AREA_TYPE.COPY
  )
  const {
    selectedDeliveryArea,
    placeId,
    coordinateGroups,
    coordinates,
    setCoordinates,
    deliverySlots,
    selectedCollectionPoint,
    setSelectedCollectionPoint,
    setSelectedDeliveryArea,
  } = useServiceAreas()
  const {
    mutateAsync: mutatePostCoordinates,
    isLoading: isLoadingPostRequest,
  } = usePostNewAreaCoordinates(placeId as string, selectedDeliveryArea.id)
  const {
    mutateAsync: mutateEditCoordinates,
    isLoading: isLoadingPatchRequest,
  } = usePatchNewAreaCoordinates(placeId as string, selectedDeliveryArea.id)
  const { mutateAsync: mutateDeleteAsync, isLoading: isLoadingDeleteRequest } =
    useDeleteArea(
      placeId as string,
      selectedDeliveryArea.id,
      deliveryAreaId as number
    )
  const {
    mutateAsync: mutateDeleteGroupAsync,
    isLoading: isLoadingDeleteGroupRequest,
  } = useDeleteAreaGroup(placeId as string, selectedDeliveryArea.id)

  const handleCloseModal = () => {
    setEditCoordinatesModal({ right: false })
  }

  const handleError = () => {
    setAlert(t('failureMessage'), 'failure', {
      vertical: 'bottom',
      horizontal: isMobile ? 'center' : 'right',
    })
  }

  useEffect(() => {
    setIsEditing(false)
    setDeliveryAreaId(null)
    setDrawnPath([])
    setDrawingModeEnabled(null)
  }, [])

  const handleCloseAlert = (
    event?: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setDeleteDialogOpen(false)
    setDeleteGroupDialogOpen(false)
  }

  const handleOpenSelectedAreaModal = (_: string, innerValue: string) => {
    if (innerValue === selectedDeliveryArea.id.toString()) {
      setSettingsModal({ right: true })
    }
    if (innerValue === selectedCollectionPoint.id.toString()) {
      setCollectionPointEditModal({ right: true })
    }
    if (innerValue === EDIT) {
      setEditCoordinatesModal({ right: true })
    }
    if (innerValue === DELETE) {
      setDeleteDialogOpen(true)
    }
    if (innerValue === DELETE_GROUP) {
      setDeleteGroupDialogOpen(true)
    }
    if (innerValue === EDIT_DELIVERY_AREA_TYPE.COPY) {
      setEditType(EDIT_DELIVERY_AREA_TYPE.COPY)
      setCopyModal({ right: true })
    }
    if (innerValue === EDIT_DELIVERY_AREA_TYPE.MOVE) {
      setEditType(EDIT_DELIVERY_AREA_TYPE.MOVE)
      setCopyModal({ right: true })
    }
  }

  const deleteDialogText = (type: string) => {
    if (type === DELETE) {
      return (
        <>
          <Typography>
            {t('serviceArea.deleteConfirmation').split('\n')[0]}
          </Typography>
          <Typography>
            {t('serviceArea.deleteConfirmation').split('\n')[1]}
          </Typography>
        </>
      )
    }
    if (type === DELETE_GROUP) {
      return (
        <Typography>{t('serviceArea.deleteAreaGroupConfirmation')}</Typography>
      )
    }
    return <div />
  }

  const handleDeleteArea = async () => {
    if (!deliveryAreaId || deliveryAreaId === -1) {
      setIsEditing(false)
      setDeliveryAreaId(null)
      setDrawingModeEnabled(null)
      setDrawnPath([])
      setCoordinates(
        coordinateGroups.filter(
          (coordinateGroup) =>
            coordinateGroup[0].deliveryAreaGroupId === selectedDeliveryArea.id
        )
      )
      const resetDeliveryArea = { ...selectedDeliveryArea }
      setSelectedDeliveryArea(resetDeliveryArea)
    }
    if (deliveryAreaId && deliveryAreaId !== -1) {
      await mutateDeleteAsync(
        {},
        {
          onError: handleError,
        }
      )
    }
  }

  const handleDeleteAreaGroup = async () => {
    await mutateDeleteGroupAsync(
      {},
      {
        onError: handleError,
        onSuccess: () => {
          setCoordinates(coordinateGroups || null)
          setSelectedDeliveryArea({ id: NaN, name: '' })
          setSelectedCollectionPoint({
            id: NaN,
            name: '',
            latitude: NaN,
            longitude: NaN,
          })
        },
      }
    )
  }

  const handleSubmitNewCoordinates = async () => {
    const coordinateData = (coordinates as DeliveryArea[][])[0][0]
    const values = {
      delivery_areas: [
        {
          ...(isEditing && { id: deliveryAreaId }),
          area: coordinateData.area,
          label:
            coordinateData.label && coordinateData.label.length > 0
              ? coordinateData.label
              : null,
        },
      ],
    }
    if (isEditing && (!deliveryAreaId || deliveryAreaId === -1)) {
      await mutatePostCoordinates(values, {
        onError: handleError,
      })
    } else {
      await mutateEditCoordinates(values, {
        onError: handleError,
      })
    }
  }

  const handleSubmitDrawnCoordinates = async () => {
    if (drawnPath.length === 0) return
    const label = coordinates?.[0]?.[0]?.label
    const areas = drawnPath.map((path) => {
      return {
        ...(isEditing && { id: deliveryAreaId }),
        area: path.map((p) => [p.lat, p.lng]),
        label: label && label.length > 0 ? label : null,
      }
    })
    const values = {
      delivery_areas: areas,
    }
    if (deliveryAreaId && deliveryAreaId !== -1) {
      await mutateEditCoordinates(values, {
        onError: handleError,
      })
    } else {
      await mutatePostCoordinates(values, {
        onError: handleError,
      })
    }
  }

  // delivery area groups cannot be deleted if they have slots or delivery areas
  const listOptionsForSelectedArea: DropdownOption[] = [
    {
      label: t('settings'),
      value: selectedCollectionPoint.id
        ? selectedCollectionPoint.id.toString()
        : selectedDeliveryArea.id.toString(),
    },
    ...(selectedDeliveryArea.id &&
    (!deliverySlots ||
      (deliverySlots?.length === 0 &&
        hasNoDeliveryAreas(coordinateGroups, selectedDeliveryArea)))
      ? [
          {
            label: t(`serviceArea.deleteAreaGroup`, {
              selectedDeliveryGroup: selectedDeliveryArea.name,
            }),
            value: DELETE_GROUP,
          },
        ]
      : []),
  ]

  const listOptionsForEditingMode: DropdownOption[] = [
    {
      label: t('editDeliveryArea'),
      value: EDIT,
    },
    {
      label:
        isEditing && deliveryAreaId && deliveryAreaId !== -1
          ? t(`serviceArea.deleteArea`, {
              selectedDeliveryArea: deliveryAreaId,
            })
          : t(`serviceArea.deleteArea`, { selectedDeliveryArea: '' }),
      value: DELETE,
    },
  ]

  if (isEditing && deliveryAreaId && deliveryAreaId !== -1) {
    listOptionsForEditingMode.splice(
      1,
      0,
      {
        label: t(`copyArea`, {
          selectedDeliveryArea: deliveryAreaId,
        }),
        value: EDIT_DELIVERY_AREA_TYPE.COPY,
      },
      {
        label: t(`moveArea`, {
          selectedDeliveryArea: deliveryAreaId,
        }),
        value: EDIT_DELIVERY_AREA_TYPE.MOVE,
      }
    )
  }

  const deleteAreaProps = {
    title:
      isEditing && deliveryAreaId && deliveryAreaId !== -1
        ? t(`serviceArea.deleteArea`, {
            selectedDeliveryArea: deliveryAreaId,
          })
        : t(`serviceArea.deleteArea`, { selectedDeliveryArea: '' }),
    text: deleteDialogText(DELETE),
    dialogOpen: deleteDialogOpen,
    handleClose: handleCloseAlert,
    handleConfirm: handleDeleteArea,
    cancelText: t('cancelDelete'),
    confirmText: t('confirmDelete'),
  }

  const deleteAreaGroupProps = {
    title: t('serviceArea.deleteArea', {
      selectedDeliveryArea: `group ${selectedDeliveryArea.name}`,
    }),
    text: deleteDialogText(DELETE_GROUP),
    dialogOpen: deleteGroupDialogOpen,
    handleClose: handleCloseAlert,
    handleConfirm: handleDeleteAreaGroup,
    cancelText: t('cancelDelete'),
    confirmText: t('confirmDelete'),
  }

  if (isLoadingDeleteRequest || isLoadingDeleteGroupRequest) {
    return <LoadingIndicator />
  }
  return (
    <>
      <ServiceAreaSettingsModal
        modal={settingsModal}
        setModal={setSettingsModal}
      />
      <EditCoordinatesModal
        handleCloseModal={handleCloseModal}
        modal={editCoordinatesModal}
        setModal={setEditCoordinatesModal}
        setIsEditing={setIsEditing}
        drawnPath={drawnPath}
        deleteShapes={deleteShapes}
        overlays={overlays}
        setDrawnPath={setDrawnPath}
        deliveryAreaId={deliveryAreaId}
      />
      <Dialog {...deleteAreaProps} />
      <Dialog {...deleteAreaGroupProps} />
      {Boolean(selectedDeliveryArea.id) && (
        <>
          <CopyAreaModal
            currentDeliveryAreaId={deliveryAreaId ?? -1}
            type={editType}
            placeId={placeId}
            modal={copyModal}
            handleCloseModal={() => setCopyModal({ right: false })}
          />
          <Toolbar
            handleClick={
              drawnPath && drawnPath.length > 0
                ? handleSubmitDrawnCoordinates
                : handleSubmitNewCoordinates
            }
            isLoadingPostRequest={isLoadingPostRequest || isLoadingPatchRequest}
            listOptions={
              isEditing || Boolean(drawingModeEnabled)
                ? listOptionsForEditingMode
                : listOptionsForSelectedArea
            }
            handleOpenModal={handleOpenSelectedAreaModal}
            saveBtnEnabled={Boolean(
              (drawnPath.length > 0 && drawingModeEnabled) || isEditing
            )}
            saveBtnVisible={Boolean(drawingModeEnabled || isEditing)}
            toolbarText={selectedDeliveryArea.name}
          />
        </>
      )}
      {Boolean(selectedCollectionPoint.id) && (
        <>
          <Toolbar
            handleOpenModal={handleOpenSelectedAreaModal}
            isLoadingPostRequest={false}
            listOptions={listOptionsForSelectedArea}
            saveBtnVisible={false}
            saveBtnEnabled={false}
            toolbarText={selectedCollectionPoint.name}
          />
          <EditCollectionPointModal
            modal={collectionPointEditModal}
            setModal={setCollectionPointEditModal}
            placeId={placeId || ''}
          />
        </>
      )}
    </>
  )
}
